<script setup>
import {ref, onMounted, computed, defineProps, watch, reactive} from 'vue'
import axios from 'axios'
import useFilterStore from '../../store/filterStore'
import useGlobalStore from '../../store/globalStore'
import regionDropdown from './RegionDropdown.vue'
import districtDropdown from './DistrictDropdown.vue'
import SuburbDropdown from './SuburbDropdown.vue'

const props = defineProps({
  type: {
    type: String,
    default: 'village',
  },
  operatorName: {
    type: String,
    default: '',
  },
  regionName: {
    type: String,
    default: '',
  },
  districtName: {
    type: String,
    default: '',
  },
  selectedRegion: {
    default: 0,
  },
  selectedDistrict: {
    default: 0,
  },
  selectedSuburb: {
    default: 0,
  },
  selectedSuburbs: {
    type: Array,
    default: () => [],
  },
  selectedHomeTypes: {
    type: Array,
    default: () => [],
  },
  selectedSizes: {
    type: Array,
    default: () => [],
  },
  defaultSuburbSelectAll: {
    default: 0,
  },
  subTitle: {
    type: String,
    default: 'Show Retirement Villages in',
  },
  selectedPriceRange: {
    type: Array,
    default: () => [],
  },
  eventUrl: {
    type: String,
    default: '',
  },
  eventDateFilter: {
    type: String,
    default: '',
  },
});

const filterStore = useFilterStore()
const globalStore = useGlobalStore()

// Data properties
const showMobileFilters = ref(false)
const showLevelOfCare = ref(false)
const showLevelOfCareFull = ref(false)
const showSize = ref(false)
const showTypeOfRooms = ref(false)
const typeOfRoomsList = ref([])
const showPriceRange = ref(false)
const priceList = ref([])
const levelCareList = ref([])
const homeTypes = ref([])
const suburbSelectAll = ref(props.defaultSuburbSelectAll)
const windowSize = ref('d')

const inputs = reactive({
  priceRangeInput: props.selectedPriceRange,
  sizeInput: props.selectedSizes,
  typeOfHomesInput: props.selectedHomeTypes,
  levelOfCareInput: [],
  roomTypeInput: [],
})

const sizeList = ref([
  {
    id: 1,
    title: '50 beds or less',
    labelId: 'size_1'
  },
  {
    id: 2,
    title: '100 beds or less',
    labelId: 'size_2'
  },
  {
    id: 3,
    title: 'Over 100 beds',
    labelId: 'size_3'
  }
])

// Computed properties
const headerTitle = computed(() => {
  if (props.type === 'operator') {
    return props.operatorName
  }

  let title = 'Retirement Villages'

  if (props.type === 'careHomes' || props.type === 'compare') {
    title = 'Rest Homes'
  }

  if (props.type === 'properties') {
    title = 'Homes for sale'
  }

  if (props.regionName) {
    return `${title} ${props.regionName}`
  }

  return `${title} ${props.districtName}`
});

const mainClass = computed(() => {
  return props.type === 'careHomes' ||  props.type === 'compare' ? 'care-homes__filters care-homes__filters--bg-ming pb-lg-8' : 'villages__filters'
});

// Methods
const applyFilter = () => {
  const region = filterStore.region.url
  const district = filterStore.district.url
  const suburbs = filterStore.suburbs
  let urlString = `${window.location.protocol}//${window.location.hostname}/available-properties/`

  if (props.type === 'villages') {
    urlString = `${window.location.protocol}//${window.location.hostname}/retirement-villages/`
  } else if (props.type === 'operator') {
    const operatorUrl = props.operatorName.toString().replace(/\s+/g, '-').toLowerCase()
    urlString = `${window.location.protocol}//${window.location.hostname}/operator/${operatorUrl}/`
  } else if (props.type === 'properties') {
    urlString = `${window.location.protocol}//${window.location.hostname}/available-properties/`
  } else if (props.type === 'careHomes' || props.type === 'compare') {
    urlString = `${window.location.protocol}//${window.location.hostname}/rest-homes/`
  } else if (props.type === 'events') {
    urlString = `${window.location.protocol}//${window.location.hostname}/${props.eventUrl}/open-days/`
  }

  if (suburbs.length === 1) {
    const suburbSlug = strSlug(suburbs[0]);
    urlString = `${urlString}${region}/${district}/${suburbSlug}`
  } else if (district !== '') {
    urlString = `${urlString}${region}/${district}`
  } else if (region !== '') {
    urlString = `${urlString}${region}`
  }

  urlString += `?`;

  if (inputs.typeOfHomesInput.length > 0) {
    urlString += `&home-types=${inputs.typeOfHomesInput}`
  }

  if (inputs.priceRangeInput.length > 0) {
    urlString += `&price-range=${inputs.priceRangeInput}`;
  }

  if (inputs.levelOfCareInput.length > 0) {
    urlString += `&level-care=${inputs.levelOfCareInput}`
  }

  // if (this.$refs.inputTab !== undefined && this.$refs.inputTab.value) {
  //   urlString += `&tab=${this.$refs.inputTab.value}`;
  // }

  if (suburbs.length > 1) {
    urlString += `&suburbs=${JSON.stringify(suburbs)}`
  }

  // if (suburbSelectAll.value) {
  //   urlString += '&suburbAllSelected=true'
  // }

  if (inputs.sizeInput.length > 0) {
    urlString += `&size=${inputs.sizeInput}`
  }

  if (inputs.roomTypeInput.length > 0) {
    urlString += `&type-of-rooms=${inputs.roomTypeInput}`
  }

  window.location.href = urlString
};

const strSlug = (text)  => {
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
}

const toggleMobileFilters = () => {
  showMobileFilters.value = !showMobileFilters.value

  if (showMobileFilters.value) {
    document.body.classList.add('stop-scrolling')
  } else {
    document.body.classList.remove('stop-scrolling')
  }
};

const toggleLevelOfCareFull = () => {
  showLevelOfCareFull.value = !showLevelOfCareFull.value
  showSize.value = false
  showTypeOfRooms.value = false
}

const clearFilters = (input, apply = true) => {
  inputs[input] = []

  if (apply) {
    applyFilter()
  }
}

const mobileClearFilters = () => {
  clearFilters('typeOfHomesInput', false)
  clearFilters('roomTypeInput', false)
  clearFilters('priceRangeInput', false)
  clearFilters('levelOfCareInput', false)
  clearFilters('sizeInput', false)

  applyFilter()
}

const toggleSize = () => {
  showSize.value = !showSize.value
  showTypeOfRooms.value = false
  showLevelOfCare.value = false
  showLevelOfCareFull.value = false
};

const toggleTypeOfRooms = () => {
  showTypeOfRooms.value = !showTypeOfRooms.value
  showSize.value = false
  showLevelOfCare.value = false
  showLevelOfCareFull.value = false
};

const toggleLevelOfCare = () => {
  showLevelOfCare.value = !showLevelOfCare.value
  showTypeOfRooms.value = false
  showSize.value = false
};

const togglePriceRange = () => {
  showPriceRange.value = !showPriceRange.value
  showTypeOfRooms.value = false
};

onMounted(() => {
  let queryString = window.location.search
  let urlParams = new URLSearchParams(queryString)

  if (urlParams.has('level-care')) {
    inputs.levelOfCareInput = urlParams.get('level-care').split(',')
  }

  if (urlParams.has('size')) {
    inputs.sizeInput = urlParams.get('size').split(',')
  }

  if (urlParams.has('type-of-rooms')) {
    inputs.roomTypeInput = urlParams.get('type-of-rooms').split(',')
  }

  if (props.type === 'careHomes') {
    axios.get(`/api/type-of-rooms`).then((response) => {
      typeOfRoomsList.value = response.data
    })

    axios.get(`/api/level-of-cares-care-homes`).then((response) => {
      levelCareList.value = response.data
    })
  } else if (props.type === 'properties') {
    axios.get(`/api/price-list`).then((response) => {
      priceList.value = response.data
    })

    axios.get(`/api/home-types`).then((response) => {
      homeTypes.value = response.data
    })
  } else {
    axios.get(`/api/level-of-cares`).then((response) => {
      levelCareList.value = response.data
    })

    axios.get(`/api/home-types`).then((response) => {
      homeTypes.value = response.data
    })
  }

  if (window.innerWidth <= 1199 ) {
    windowSize.value = 'lg'
  } else if (window.innerWidth <= 991 ) {
    windowSize.value = 'm'
  }
});

watch(
  () => inputs.typeOfHomesInput,
  (newInput) => {
    globalStore.isMobile ? null : applyFilter()
  }
);


</script>
<template>
  <div>
    <div :class="mainClass">
      <div class="container content position-relative">
        <div v-if="type === 'events'">
          <h1 class="content__header-title color-white ">Open Days & Events</h1>
          <div class="mb-6">
            <a
              class="d-inline-flex home-search__link mr-2"
              :class="{ 'home-search__link--selected': eventUrl === 'retirement-villages' }"
              href="/retirement-villages/open-days"
            >
              Retirement Villages
            </a>
            <a
              class="d-inline-flex home-search__link"
              :class="{ 'home-search__link--selected': eventUrl === 'rest-homes' }"
              href="/rest-homes/open-days"
            >
              Rest Homes
            </a>
          </div>
        </div>
        <div v-else>
          <div class="d-flex flex-row">
            <h1 class="content__header-title w-auto">{{ headerTitle }}</h1>
            <span class="mt-1 color-white" v-if="props.type === 'properties'">*</span>
          </div>
          <p class="villages__filters-intro d-none d-xl-block">{{ props.subTitle }}</p>
        </div>
        <ul class="care-homes__filter-list">
          <li class="care-homes__filter-item">
            <region-dropdown
              id="region_list"
              class="dropdown--village dropdown--full-width"
              :selected-id="selectedRegion"
              :empty-string="'All Regions'"
              icon-class="icon--arrow-down"
              :type="type"
              :event-type="eventUrl"
            ></region-dropdown>
          </li>
          <li class="care-homes__filter-item">
            <district-dropdown
              id="district_list"
              class="dropdown--village dropdown--full-width"
              :default-selected-id="selectedDistrict"
              :empty-string="'All Districts'"
              icon-class="icon--arrow-down"
              :region-id="selectedRegion"
              :default-type="type"
              :event-type="eventUrl"
            ></district-dropdown>
          </li>
          <li class="care-homes__filter-item" v-if="type !== 'events'">
            <suburb-dropdown
              id="district_list"
              class="dropdown--village dropdown--suburb"
              :district-id="selectedDistrict"
              :empty-string="'All suburbs'"
              icon-class="icon--arrow-down"
              :default-type="type"
              :default-checked-options="selectedSuburbs"
            ></suburb-dropdown>
          </li>
          <li class="care-homes__filter-item">
            <button
              type="submit"
              class="btn btn--auto-width btn--green"
              @click.prevent="applyFilter">
              Search
            </button>
          </li>
        </ul>
      </div>
    </div>
    <div class="bg-white" v-sticky v-if="type !== 'events' && type !== 'compare'">
      <div class="container content position-relative">
        <div  class="villages__mobile-filter-star villages__mobile-filter-star--property d-lg-none">
          <button type="button" @click.prevent="toggleMobileFilters" class="list-filter__mobile-filter font-weight-bolder">
            <i class="list-filter__icon"></i> Filter results <i class="list-filter__icon-chev"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="list-filter d-none d-lg-block" v-if="type !== 'events' && type !== 'compare'">
      <div class="container">
        <ul class="list-filter__list">
          <li class="d-flex align-items-center w-100 d-sm-none">
            <a href="#" @click.prevent="toggleMobileFilters" class="list-filter__mobile-filter font-weight-bolder">
              <i class="list-filter__icon"></i> Filter results <i class="list-filter__icon-chev"></i>
            </a>
          </li>
          <li class="list-filter__item checkbox-custom d-flex 1" v-for="homeType in homeTypes" :key="homeType.id" v-if="type !== 'careHomes'">
            <input
              type="checkbox"
              class="typeOfHomes"
              :id="`homeType_${homeType.id}`"
              :value="homeType.id"
              v-model="inputs.typeOfHomesInput"
            >
            <label class="form-check-label mb-0" :for="`homeType_${homeType.id}`"> {{ homeType.name }}</label>
          </li>
          <li class="list-filter__item list-filter__item--border position-relative d-none d-sm-flex" v-if="type === 'villages' || type === 'operator'">
            <button
              class="btn btn--dropdown position-relative"
              :class="{ 'btn--active': inputs.levelOfCareInput.length > 0 || showLevelOfCare}"
              @click.prevent="toggleLevelOfCare"
            >
              Level of care <i class="icon icon--arrow-down" :class="{ 'icon--flipped': showLevelOfCare}"></i>
            </button>
            <transition name="fade">
              <div class="filter filter--care-home" v-if="showLevelOfCare" v-cloak v-click-away="toggleLevelOfCare">
                <ul class="filter__list">
                  <li class="filter__item checkbox-custom" v-show="levelCare.is_higher"  v-for="levelCare in levelCareList" :key="levelCare.id">
                    <input
                      type="checkbox"
                      class="levelOfCare"
                      :id="`levelOfCare_${levelCare.id}`"
                      name="levelOfCare[]"
                      :value="levelCare.id"
                      v-model="inputs.levelOfCareInput"
                    >
                    <label class="form-check-label mb-0" :for="`levelOfCare_${levelCare.id}`"> {{ levelCare.name }}</label>
                  </li>
                </ul>
                <a href="#" class="filter__link filter__link--clear" @click.prevent="clearFilters('levelOfCareInput')">Clear</a>
                <a href="#" class="filter__link filter__link--apply" @click.prevent="applyFilter">Apply</a>
              </div>
            </transition>
          </li>
          <!-- Care homes !-->
          <li
            class="list-filter__item checkbox-custom"
            v-show="index < 5"
            v-if="type === 'careHomes' && windowSize === 'd'"
            v-for="(levelOfCare, index) in levelCareList"
            :key="levelOfCare.id"
          >
            <input
              type="checkbox"
              class="typeOfHomes"
              :id="`level_of_care_${levelOfCare.id}`"
              :value="levelOfCare.id"
              v-model="inputs.levelOfCareInput"
              @change="applyFilter"
            >
            <label class="form-check-label mb-0" :for="`level_of_care_${levelOfCare.id}`"> {{ levelOfCare.name }}</label>
          </li>
          <li class="list-filter__item position-relative" v-if="type === 'careHomes' && windowSize === 'd'" >
            <button class="btn btn--dropdown position-relative" @click.prevent="toggleLevelOfCare">
              More <i class="icon icon--arrow-down" :class="{ 'icon--flipped': showLevelOfCare}"></i>
            </button>
            <transition name="fade">
              <div class="filter filter--care-home" v-if="showLevelOfCare" v-cloak v-click-away="toggleLevelOfCare">
                <ul class="filter__list">
                  <li class="filter__item checkbox-custom" v-for="(levelOfCare, index) in levelCareList" :key="levelOfCare.id">
                    <div v-if="index > 4">
                      <input
                        type="checkbox"
                        class="typeOfHomes"
                        :id="`level_of_care_2${levelOfCare.id}`"
                        :value="levelOfCare.id"
                        v-model="inputs.levelOfCareInput"
                      >
                      <label class="form-check-label mb-0" :for="`level_of_care_2${levelOfCare.id}`"> {{ levelOfCare.name }}</label>
                    </div>
                  </li>
                </ul>
                <a href="#" class="filter__link filter__link--apply" @click.prevent="applyFilter">Apply</a>
              </div>
            </transition>
          </li>
          <li
            class="list-filter__item position-relative"
            v-if="type === 'careHomes' && windowSize === 'lg'"
            v-cloak
          >
            <button
              class="btn btn--dropdown position-relative"
              :class="{ 'btn--active': levelCareList.length > 0 || showLevelOfCareFull}"
              @click.prevent="toggleLevelOfCareFull"
            >
              Level of care <i class="icon icon--arrow-down" :class="{ 'icon--flipped': showLevelOfCareFull}"></i>
            </button>
            <transition name="fade">
              <div
                class="filter filter--care-home"
                style="left: 0; right: auto"
                v-if="showLevelOfCareFull"
                v-cloak
                v-click-away="toggleLevelOfCareFull"
              >
                <ul class="filter__list">
                  <li class="filter__item checkbox-custom" v-for="(levelOfCare, index) in levelCareList" :key="levelOfCare.id">
                      <input
                        type="checkbox"
                        class="typeOfHomes"
                        :id="`level_of_care_2${levelOfCare.id}`"
                        :value="levelOfCare.id"
                        v-model="inputs.levelOfCareInput"
                      >
                      <label class="form-check-label mb-0" :for="`level_of_care_2${levelOfCare.id}`"> {{ levelOfCare.name }}</label>
                  </li>
                </ul>
                <a href="#" class="filter__link filter__link--clear" @click.prevent="clearFilters('levelOfCareInput')">Clear</a>
                <a href="#" class="filter__link filter__link--apply" @click.prevent="applyFilter">Apply</a>
              </div>
            </transition>
          </li>

          <li class="list-filter__item list-filter__item--border position-relative d-none d-sm-flex" v-if="type === 'careHomes'">
            <button
              class="btn btn--dropdown position-relative"
              :class="{ 'btn--active': selectedSizes.length > 0 || showSize}"
              @click.prevent="toggleSize"
            >
              Size of care home <i class="icon icon--arrow-down" :class="{ 'icon--flipped': showSize}"></i>
            </button>
            <transition name="fade">
              <div class="filter filter--care-home" v-if="showSize" v-cloak v-click-away="toggleSize">
                <ul class="filter__list">
                  <li class="filter__item checkbox-custom" v-for="size of sizeList" :key="size.id">
                    <input
                      type="checkbox"
                      class="size"
                      :id="size.labelId"
                      :value="size.id"
                      v-model="inputs.sizeInput"
                    >
                    <label class="form-check-label mb-0" :for="size.labelId" v-text="size.title"></label>
                  </li>
                </ul>
                <a href="#" class="filter__link filter__link--clear" @click.prevent="clearFilters('sizeInput')">Clear</a>
                <a href="#" class="filter__link filter__link--apply" @click.prevent="applyFilter">Apply</a>
              </div>
            </transition>
          </li>
          <li class="list-filter__item list-filter__item--border position-relative d-none d-sm-flex" v-if="type === 'careHomes'">
            <button
              class="btn btn--dropdown position-relative"
              :class="{ 'btn--active': typeOfRoomsList.length > 0 || showTypeOfRooms }"
              @click.prevent="toggleTypeOfRooms"
            >
              Accommodation <i class="icon icon--arrow-down" :class="{ 'icon--flipped': showTypeOfRooms}"></i>
            </button>
            <transition name="fade">
              <div class="filter filter--care-home" v-if="showTypeOfRooms" v-cloak v-click-away="toggleTypeOfRooms">
                <ul class="filter__list">
                  <li class="filter__item checkbox-custom" v-for="typOfRoom in typeOfRoomsList" :key="typOfRoom.id">
                    <input
                      type="checkbox"
                      class="typeOfRooms"
                      :id="`typeOfRooms_${typOfRoom.id}`"
                      :value="typOfRoom.id"
                      v-model="inputs.roomTypeInput"
                    >
                    <label class="form-check-label mb-0" :for="`typeOfRooms_${typOfRoom.id}`"> {{ typOfRoom.name }}</label>
                  </li>
                </ul>

                <a href="#" class="filter__link filter__link--clear" @click.prevent="clearFilters('roomTypeInput')">Clear</a>
                <a href="#" class="filter__link filter__link--apply" @click.prevent="applyFilter">Apply</a>
              </div>
            </transition>
          </li>
          <!-- care homes end !-->
          <!-- hfs start !-->
          <li class="list-filter__item list-filter__item--border position-relative d-none d-sm-flex" v-if="type === 'properties'">
            <button
              class="btn btn--dropdown position-relative"
              :class="{ 'btn--active': priceList.length > 0 || showPriceRange }"
              @click.prevent="showPriceRange = !showPriceRange"
            >
              Price range <i class="icon icon--arrow-down" :class="{ 'icon--flipped': showPriceRange}"></i>
            </button>
            <transition name="fade">
              <div class="filter filter--care-home" v-if="showPriceRange" v-cloak v-click-away="togglePriceRange">
                <ul class="filter__list">
                  <li class="filter__item checkbox-custom" v-for="price in priceList" :key="price.id">
                    <input
                      type="checkbox"
                      class="priceRange"
                      :id="`priceRange_${price.id}`"
                      :value="price.id"
                      v-model="inputs.priceRangeInput"
                    >
                    <label class="form-check-label mb-0" :for="`priceRange_${price.id}`"> {{ price.name }}</label>
                  </li>
                </ul>

                <a href="#" class="filter__link filter__link--clear" @click.prevent="clearFilters('priceRangeInput')">Clear</a>
                <a href="#" class="filter__link filter__link--apply" @click.prevent="applyFilter">Apply</a>
              </div>
            </transition>
          </li>
          <!-- hfs end !-->
        </ul>
      </div>
    </div>
    <div class="list-filter list-filter--events" v-if="type === 'events'">
      <div class="container">
        <ul class="list-filter__list justify-content-start">
          <li class="list-filter__item">
            <span class="fs-14 font-weight-light-bold" v-if="eventDateFilter === 'all' || eventDateFilter === ''">All dates</span>
            <a href="?date-filter=all" class="fs-14 font-weight-light-bold" v-else>All dates</a>
          </li>
          <li class="list-filter__item list-filter__item--border">
            <span class="fs-14 font-weight-light-bold" v-if="eventDateFilter === 'today'">Today</span>
            <a href="?date-filter=today" class="fs-14 font-weight-light-bold" v-else>Today</a>
          </li>
          <li class="list-filter__item list-filter__item--border">
            <span class="fs-14 font-weight-light-bold" v-if="eventDateFilter === 'this-week'">This week</span>
            <a href="?date-filter=this-week" class="fs-14 font-weight-light-bold" v-else>This week</a>
          </li>
          <li class="list-filter__item list-filter__item--border">
            <span class="fs-14 font-weight-light-bold" v-if="eventDateFilter === 'next-week'">Next week</span>
            <a href="?date-filter=next-week" class="fs-14 font-weight-light-bold" v-else>Next week</a>
          </li>
          <li class="list-filter__item list-filter__item--border">
            <span class="fs-14 font-weight-light-bold" v-if="eventDateFilter === 'next-month'">This month</span>
            <a href="?date-filter=this-month" class="fs-14 font-weight-light-bold" v-else>This month</a>
          </li>
        </ul>
      </div>
    </div>
    <!-- Mobile filters !-->
    <transition name="slide-fade-right">
      <div class="villages__mobile-filters" v-if="showMobileFilters" v-cloak>
        <div class="mobile-filters__header">
          <a href="#" class="mobile-filters__icon-text"><i class="villages__mobile-filter-icon"></i>Filters</a>
          <a href="#" class="mobile-filters__clear" @click.prevent="mobileClearFilters">Clear all</a>
          <a href="#" @click.prevent="toggleMobileFilters" class="mobile-filters__close"><i class="icon icon--cross"></i></a>
        </div>
        <div class="mobile-filters__body">
          <div v-if="type === 'villages' || type === 'operator' || type === 'properties'">
              <div class="mobile-filters__title">Type of home</div>
              <ul class="filter__list">
                <li class="filter__item checkbox-custom mb-3" v-for="homeType in homeTypes" :key="homeType.id">
                  <input
                    type="checkbox"
                    class="typeOfHomes"
                    :id="`homeType_${homeType.id}`"
                    :value="homeType.id"
                    v-model="inputs.typeOfHomesInput"
                    data-mobile="true"
                  >
                  <label class="form-check-label mb-0 fs-16" :for="`homeType_${homeType.id}`"> {{ homeType.name }}</label>
                </li>
              </ul>
          </div>
          <div v-if="type === 'villages' || type === 'operator' || type === 'careHomes'">
            <div class="mobile-filters__title">Levels of care</div>
            <ul class="filter__list">
              <li class="filter__item checkbox-custom mb-3" v-show="type === 'careHomes' || levelCare.is_higher " v-for="levelCare in levelCareList" :key="levelCare.id">
                <input
                  type="checkbox"
                  class="levelOfCare"
                  :id="`level_of_care_${levelCare.id}`"
                  :value="levelCare.id"
                  v-model="inputs.levelOfCareInput"
                >
                <label class="form-check-label mb-0 fs-16" :for="`level_of_care_${levelCare.id}`"> {{ levelCare.name }}</label>
              </li>
            </ul>
          </div>

          <div v-if="type === 'properties'">
            <div class="mobile-filters__title">Price range</div>
            <ul class="filter__list">
              <li class="filter__item checkbox-custom mb-3" v-for="price in priceList" :key="price.id">
                <input
                  type="checkbox"
                  class="priceRange"
                  :id="`priceRange_${price.id}`"
                  :value="price.id"
                  v-model="inputs.priceRangeInput"
                  data-mobile="true"
                >
                <label class="form-check-label mb-0 fs-16" :for="`priceRange_${price.id}`"> {{ price.name }}</label>
              </li>
            </ul>
          </div>

          <div v-if="type === 'careHomes'">
            <div class="mobile-filters__title">Size of Care Home</div>
            <ul class="filter__list">
              <li class="filter__item checkbox-custom mb-3" v-for="size in sizeList" :key="size.id">
                <input
                  type="checkbox"
                  class="size"
                  :id="size.labelId"
                  :value="size.id"
                  v-model="inputs.sizeInput"
                  data-mobile="true"
                >
                <label class="form-check-label mb-0 fs-16" :for="size.labelId"> {{ size.title }}</label>
              </li>
            </ul>
            <div class="mobile-filters__title">Accommodation</div>
            <ul class="filter__list">
              <li class="filter__item checkbox-custom mb-3" v-for="typOfRoom in typeOfRoomsList" :key="typOfRoom.id">
                <input
                  type="checkbox"
                  class="typeOfRooms"
                  :id="`typeOfRooms_${typOfRoom.id}`"
                  :value="typOfRoom.id"
                  v-model="inputs.roomTypeInput"
                  data-mobile="true"
                >
                <label class="form-check-label mb-0 fs-16" :for="`typeOfRooms_${typOfRoom.id}`"> {{  typOfRoom.name }}</label>
              </li>
            </ul>
          </div>
        </div>
        <div class="mobile-filters__footer">
          <a href="#" class="btn btn--green text-center" @click.prevent="applyFilter">Apply</a>
        </div>
      </div>
    </transition>
    <div class="mobile-filters__background" v-if="showMobileFilters" v-cloak></div>
  </div>
</template>
